/**
 * Snapping Connection Line Component
 * 
 * Custom connection line that provides visual feedback when the connection
 * is within snapping range of a compatible handle.
 * 
 * Features:
 * - Type-based coloring (text=green, image=magenta, video=blue)
 * - Glowing effect when near a compatible handle
 * - Red color for invalid connections
 * - Smooth bezier curve path
 */

import React, { memo, useMemo } from 'react';
import { ConnectionLineComponentProps, getBezierPath, Position, useConnection } from '@xyflow/react';
import type { ConnectorType } from '../types/nodeConfig';

/**
 * Color map for connector types - matches CSS variables
 * --color-type-text: #FED402 (yellow)
 * --color-type-image: #42C3D7 (cyan)
 * --color-type-video: #FF4B42 (red)
 * --color-type-audio: #10B981 (green)
 * --color-type-any: #F59E0B (amber/orange)
 */
const CONNECTOR_TYPE_COLORS: Record<ConnectorType, { stroke: string; glow: string }> = {
  text: { stroke: 'rgba(254, 212, 2, 0.9)', glow: 'rgba(254, 212, 2, 0.6)' },    // Yellow #FED402
  image: { stroke: 'rgba(66, 195, 215, 0.9)', glow: 'rgba(66, 195, 215, 0.6)' }, // Cyan #42C3D7
  video: { stroke: 'rgba(255, 75, 66, 0.9)', glow: 'rgba(255, 75, 66, 0.6)' },   // Red #FF4B42
  audio: { stroke: 'rgba(16, 185, 129, 0.9)', glow: 'rgba(16, 185, 129, 0.6)' }, // Green #10B981
  any: { stroke: 'rgba(245, 158, 11, 0.9)', glow: 'rgba(245, 158, 11, 0.6)' }    // Amber #F59E0B
};

/**
 * Determine connector type from source node type
 */
function getSourceConnectorType(nodeType: string | undefined, _handleId: string | null): ConnectorType {
  if (!nodeType) return 'any';
  
  // For output handles (source), determine type based on node's output type
  const nodeOutputMapping: Record<string, ConnectorType> = {
    startingPrompt: 'text',
    agentPrompt: 'text',
    imagePrompt: 'image',
    gptImage: 'image',
    fluxImage: 'image',
    zImageTurbo: 'image',
    qwenImage: 'image',
    qwenImageEdit: 'image',
    ideogramImage: 'image',
    recraftImage: 'image',
    seedream: 'image',
    recraftV3: 'image',
    klingImage: 'image',
    klingVideo: 'video',
    kling26Video: 'video',
    kling25Video: 'video',
    klingO3Video: 'video',
    wanVideo: 'video',
    wan26Video: 'video',
    wanAnimateVideo: 'video',
    ltxVideo: 'video',
    ltxFastVideo: 'video',
    minimaxVideo: 'video',
    pixverseVideo: 'video',
    hunyuanVideo: 'video',
    klingAvatar: 'video',
    veoVideo: 'video',
    geminiOmni: 'video',
    seedance25Video: 'video',
    seedance2Video: 'video',
    minimaxH3Video: 'video',
    imagePanel: 'image',
    inpaint: 'image',
    documentPanel: 'text',
    dictation: 'text',
    universalOutput: 'any',
    customOutput: 'any',
    customNode: 'any'
  };
  
  return nodeOutputMapping[nodeType] || 'any';
}

const SnappingConnectionLineComponent: React.FC<ConnectionLineComponentProps> = ({
  fromX,
  fromY,
  fromPosition,
  toX,
  toY,
  toPosition,
  connectionStatus,
  toNode,
  toHandle,
}) => {
  // Get the active connection info (includes source node)
  const connection = useConnection();
  
  // Determine connector type based on source node
  const connectorType = useMemo(() => {
    // In React Flow 12+, connection.fromNode is the InternalNodeBase with type property
    const fromNodeType = connection.fromNode?.type;
    return getSourceConnectorType(fromNodeType, connection.fromHandle?.id || null);
  }, [connection.inProgress, connection.fromNode, connection.fromHandle]);
  
  // Determine if we're snapping to a handle
  const isSnapping = toNode !== null && toHandle !== null;
  
  // Determine the line color based on connection status and connector type
  let strokeColor: string;
  let glowColor: string;
  
  if (isSnapping && connectionStatus === 'invalid') {
    // Invalid connection - always red
    strokeColor = 'rgba(220, 100, 100, 0.9)';
    glowColor = 'rgba(220, 100, 100, 0.6)';
  } else {
    // Use connector type color for valid/pending connections
    const typeColors = CONNECTOR_TYPE_COLORS[connectorType];
    strokeColor = typeColors.stroke;
    glowColor = typeColors.glow;
  }

  // Calculate the bezier path
  const [path] = getBezierPath({
    sourceX: fromX,
    sourceY: fromY,
    sourcePosition: fromPosition,
    targetX: toX,
    targetY: toY,
    targetPosition: toPosition || Position.Left,
  });

  return (
    <g>
      {/* Glow effect layer (wider, blurred) */}
      {isSnapping && (
        <path
          fill="none"
          stroke={glowColor}
          strokeWidth={12}
          strokeLinecap="round"
          d={path}
          style={{
            filter: 'blur(4px)',
          }}
        />
      )}
      
      {/* Main connection line */}
      <path
        fill="none"
        stroke={strokeColor}
        strokeWidth={3}
        strokeLinecap="round"
        d={path}
        style={{
          transition: 'stroke 0.15s ease, stroke-width 0.15s ease',
        }}
      />
      
      {/* Animated endpoint indicator when snapping - temporarily hidden */}
      {/* {isSnapping && (
        <circle
          cx={toX}
          cy={toY}
          r={8}
          fill={strokeColor}
          style={{
            filter: `drop-shadow(0 0 6px ${glowColor})`,
            animation: 'pulse 0.6s ease-in-out infinite alternate',
          }}
        />
      )} */}
      
      {/* Regular endpoint when not snapping */}
      {!isSnapping && (
        <circle
          cx={toX}
          cy={toY}
          r={4}
          fill={strokeColor}
        />
      )}
    </g>
  );
};

/**
 * Memoized SnappingConnectionLine to prevent re-renders during connection dragging
 */
const SnappingConnectionLine = memo(SnappingConnectionLineComponent);

export default SnappingConnectionLine;
